/* eslint-disable no-restricted-imports */
import React, { useEffect, useState } from 'react';
import { getFeatureFlags } from '@noths/polaris-client-feature-flags';
import { isBreakpointSmallOrBelow, useBreakpoint } from '@noths/polaris-client-ribbons-base';
import type { ThemeColorOrCSSVar } from '@noths/polaris-client-ribbons-design-system';
import {
  Banner,
  HeroBanner,
  IconCheckmark,
  Modal,
  SolidButton,
  StandardBanner,
} from '@noths/polaris-client-ribbons-design-system';
import { useMembership } from '@noths/polaris-client-site-chrome-ui';
import { getPathnameFromUrl } from '@noths/polaris-client-utils';
import type { CurrencyCode } from '@noths/polaris-dev-ts-types';
import type { GetServerSideProps, NextConfig } from 'next';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import type { ParsedUrlQuery } from 'querystring';

import { Seo } from 'src/components/atoms/Seo/Seo';
import { SectionContainer } from 'src/components/molecules/SectionContainer/SectionContainer';
import { ErrorNotification } from 'src/components/organisms/ErrorNotification/ErrorNotification';
import { CopyBlockSection } from 'src/components/sections/CopyBlockSection/CopyBlockSection';
import { SKIP_CONTENT_TARGET_ID } from 'src/constants/elementIds';
import { getMembershipTermsAndConditions } from 'src/content/getMembershipTermsAndConditions';
import { membershipContent } from 'src/content/static/membership';
import { useMembershipBasket } from 'src/hooks/useMembershipBasket';
import { fetchGlobalContent } from 'src/services/contentstack';
import * as styles from 'src/styles/membership.styles';
import {
  trackAddMembershipToBasket,
  trackAddMembershipToBasketError,
} from 'src/tracking/pages/membership';
import { MembershipBasketModal } from '../components/organisms/MembershipBasketModal/MembershipBasketModal';

interface MembershipPageProps {
  currency: CurrencyCode;
  termsAndConditionsContent: string;
}

// @ts-ignore
const { PROD_LIKE_ENV } = (getConfig() as NextConfig).publicRuntimeConfig;

const { contentSection, ctaText, errors, heroSection, questionsAndAnswersSection, title } =
  membershipContent;

// We don't want to show the CTA when we have arrived from Learn more links from the web checkout.
const sourceIsCheckout = (query: ParsedUrlQuery) => {
  return query?.source === 'checkout';
};

const env = PROD_LIKE_ENV ? 'production' : 'qa';

interface ButtonContent {
  alreadyMember: boolean;
  checkmarkColor: ThemeColorOrCSSVar | 'currentColor';
  membershipInBasket: boolean;
}

const ButtonContent = ({ alreadyMember, checkmarkColor, membershipInBasket }: ButtonContent) => {
  const showCheckmark = alreadyMember || membershipInBasket;

  return (
    <>
      {showCheckmark && <IconCheckmark color={checkmarkColor} css={styles.buttonCheckmarkIcon} />}
      {alreadyMember
        ? ctaText.alreadyMember
        : membershipInBasket
        ? ctaText.inBasket
        : ctaText.default}
    </>
  );
};

const MembershipPage = ({ currency, termsAndConditionsContent }: MembershipPageProps) => {
  const [basketModalOpen, setBasketModalOpen] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [alreadyMember, setAlreadyMember] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const [membershipAdded, setMembershipAdded] = useState(false);
  const breakpoint = useBreakpoint({ initializeWithLarge: true });
  const { query } = useRouter();
  const { membershipIsActive } = useMembership();
  const { addMembershipToBasket, basket, hasFetchBasketError, membershipInBasket } =
    useMembershipBasket({
      env,
      currency,
    });

  useEffect(() => {
    if (hasFetchBasketError) {
      setError(errors.FETCH_BASKET_ERROR);
    }
  }, [hasFetchBasketError]);

  useEffect(() => {
    if (membershipInBasket) {
      setMembershipAdded(true);
    }
  }, [membershipInBasket]);

  useEffect(() => {
    if (membershipIsActive) {
      setAlreadyMember(true);
    }
  }, [membershipIsActive]);

  const handleCTAClick = async () => {
    try {
      await addMembershipToBasket();

      setMembershipAdded(true);
      trackAddMembershipToBasket();

      if (isBreakpointSmallOrBelow(breakpoint)) {
        window.location.assign('/cart');
      } else {
        setBasketModalOpen(!basketModalOpen);
      }
    } catch {
      setError(errors.ADD_TO_BASKET_ERROR);
      trackAddMembershipToBasketError(errors.ADD_TO_BASKET_ERROR);
    }
  };

  const showCTAButtons = !sourceIsCheckout(query) && !error;
  const enableCTAButtons = !membershipAdded && !alreadyMember;

  return (
    <>
      {termsModalOpen && (
        <Modal
          contentLabel="NOTHS & More Terms and Conditions"
          isModalOpen={termsModalOpen}
          onModalClose={() => setTermsModalOpen(false)}
        >
          {({ Body, Footer, Header }) => {
            return (
              <>
                <Header onCloseButtonClick={() => setTermsModalOpen(false)}>
                  <h2 css={styles.tcsModalheading}>NOTHS & More Terms and Conditions</h2>
                </Header>
                <Body>
                  <div css={styles.tcsModalbody}>
                    {termsAndConditionsContent ? (
                      <CopyBlockSection alignment="left" trackingTitle="membershipTCs">
                        {termsAndConditionsContent}
                      </CopyBlockSection>
                    ) : (
                      'Sorry, we are unable to retrieve Membership Terms and conditions at the moment. Please try again later'
                    )}
                  </div>
                </Body>
                <Footer>
                  <div>
                    <SolidButton fullWidth onClick={() => setTermsModalOpen(false)}>
                      Got it
                    </SolidButton>
                  </div>
                </Footer>
              </>
            );
          }}
        </Modal>
      )}
      {basketModalOpen && basket && (
        <MembershipBasketModal
          basket={basket}
          isModalOpen={basketModalOpen}
          onModalClose={() => setBasketModalOpen(!basketModalOpen)}
        />
      )}
      <Seo description={`${heroSection.heading}. ${heroSection.copy}`} title={title} />
      {error ? (
        <>
          <ErrorNotification errorMsg={error} setErrorState={() => setError(null)} />
          <div css={styles.overlay} data-testid="overlay" />
        </>
      ) : null}
      <div css={styles.wrapper()} id={SKIP_CONTENT_TARGET_ID}>
        <HeroBanner
          backgroundColor={heroSection.backgroundColor}
          buttonBackgroundColor="#fff"
          buttonDisabled={!enableCTAButtons}
          buttonText={
            showCTAButtons && (
              <ButtonContent
                alreadyMember={alreadyMember}
                checkmarkColor="currentColor"
                membershipInBasket={membershipAdded}
              />
            )
          }
          buttonTextColorMode="brand"
          headingLine1={heroSection.heading}
          imageAlt=""
          imageHeight={216}
          imageWidth={360}
          images={[
            {
              src: membershipContent.heroSection.imageUrl,
              width: 360,
            },
          ]}
          onButtonClick={handleCTAClick}
          subHeading={heroSection.copy}
          // @ts-ignore
          textColorMode={heroSection.textColorSetting}
        />
      </div>
      <div css={styles.contentSection(membershipAdded, alreadyMember)}>
        {contentSection.map((item, index) => {
          const isTheLastItem = index === contentSection.length - 1;
          return (
            <div css={styles.wrapper(isTheLastItem)} key={item.heading}>
              <StandardBanner
                backgroundColor={item.backgroundColor}
                copy={item.copy}
                heading={item.heading}
                imageAlt=""
                imageHeight={216}
                imageWidth={360}
                images={[
                  {
                    src: item.imageUrl,
                    width: 360,
                  },
                ]}
              />
            </div>
          );
        })}
      </div>
      {showCTAButtons && (
        <SectionContainer columnsXL={10} maxWidth="full" wrapperStyles={styles.ctaButtonWrapper}>
          <SolidButton disabled={!enableCTAButtons} onClick={handleCTAClick}>
            <ButtonContent
              alreadyMember={alreadyMember}
              checkmarkColor="currentColor"
              membershipInBasket={membershipAdded}
            />
          </SolidButton>
        </SectionContainer>
      )}
      <div css={styles.tcsContainer} id="terms">
        <Banner backgroundColor="#bbbbbe" roundedCorners>
          <div css={styles.tcsContent}>
            <p>
              Membership can only be used by the purchaser. By signing up to NOTHS & More,
              you&apos;re agreeing to the membership{' '}
              <button onClick={() => setTermsModalOpen(true)}>terms and conditions</button> and to
              receiving exclusive offers and content. You can unsubscribe at any time.
            </p>
          </div>
        </Banner>
      </div>
      <div css={styles.faqsContainer}>
        <h4 css={styles.questionsSectionTitle}>{questionsAndAnswersSection.title}</h4>
        {questionsAndAnswersSection.questionsAndAnswers.map((item) => (
          <div css={styles.questionsContainer} key={item.question}>
            <b>{item.question}</b>
            <p css={styles.answers}>{item.answer}</p>
          </div>
        ))}
        <div css={styles.questionsContainer} key={'What is the cancellation policy?'}>
          <b>What is the cancellation policy?</b>
          <p css={styles.answers}>
            So long as you have not made any use of the NOTHS & More discount, you can cancel NOTHS
            & More within 14 days, starting the day after the service has been bought. You can
            request cancellation by contacting Customer Services on {''}
            <a
              css={styles.linkText}
              href="https://info.notonthehighstreet.com/hc/en-us/requests/new"
              rel="nofollow noreferrer"
              target="_blank"
            >
              this form
            </a>
            . Please note, if you have used the NOTHS & More discount you will no longer be entitled
            to cancel or receive a refund.
          </p>
        </div>
      </div>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { query, req } = context;
  const { currency = 'GBP' } = req.cookies;
  const featureFlags = (req && getFeatureFlags(req)) || {};
  const pathname = getPathnameFromUrl(req.url ?? '');

  const [termsAndConditionsContent, [navData, topBannerData]] = await Promise.all([
    getMembershipTermsAndConditions(featureFlags),
    fetchGlobalContent({
      featureFlags,
      urlPath: pathname,
    }),
  ]);
  const mergeBasket = !sourceIsCheckout(query);

  return {
    props: {
      currency,
      pageEventCategory: 'Membership',
      navData,
      topBannerData,
      mergeBasket,
      termsAndConditionsContent,
    },
  };
};

export default MembershipPage;
